import React from 'react';
import { useI18n } from '../../i18n/I18nContext';
import './LoadingState.css';

/**
 * Loading states (spec §3.12). A page, a table or a list loads as a SKELETON shaped like what
 * is coming — never as a lone spinner; spinners (16px) belong inside buttons and tiny async spots.
 * Everything fades in after 150ms (a fast response never flashes), and is announced as busy.
 *
 * Skeleton screens (content with a known shape):
 *   <LoadingState skeleton="page" />            // header + KPI tiles + table card
 *   <LoadingState skeleton="table" rows={6} />  // a table card
 *   <LoadingState skeleton="cards" count={6} /> // a card grid
 *   <LoadingState skeleton="list" rows={5} />   // list rows
 *   <LoadingState skeleton="form" rows={4} />   // labelled fields
 *   <LoadingState skeleton="text" lines={3} />
 *
 * A small async spot (no known shape) — the indicator + a short message:
 *   <LoadingState compact message="Yuklanmoqda..." />
 *   <LoadingState variant="dots" />  /  variant="bar"  /  variant="pulse"
 *
 * Modifiers: compact — tighter padding for inline use; fullscreen — a fixed overlay for
 * app-level transitions.
 */
function LoadingState({
  message,
  variant = 'spinner',
  skeleton = null,
  rows,
  count,
  lines = 3,
  compact = false,
  fullscreen = false,
}) {
  const { t } = useI18n();
  const resolvedMessage = message ?? t('ui.loading');
  const a11y = {
    role: 'status',
    'aria-live': 'polite',
    'aria-busy': true,
    'aria-label': resolvedMessage,
  };

  if (skeleton) {
    return (
      <div
        className={`ls-skel-wrap ${compact ? 'is-compact' : ''}`.trim()}
        {...a11y}
      >
        {/* `rows` and `count` are aliases: a list / table takes either, a card grid prefers `count`. */}
        {renderSkeleton(skeleton, { rows: rows ?? count ?? 5, count: count ?? rows ?? 6, lines })}
        <span className="visually-hidden">{resolvedMessage}</span>
      </div>
    );
  }

  const cls = [
    'loading-state',
    compact ? 'is-compact' : '',
    fullscreen ? 'is-fullscreen' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} {...a11y}>
      {renderIndicator(variant)}
      {resolvedMessage ? <p className="loading-message">{resolvedMessage}</p> : null}
    </div>
  );
}

function renderIndicator(variant) {
  switch (variant) {
    case 'dots':
      return (
        <div className="loading-dots" aria-hidden="true">
          <span /><span /><span />
        </div>
      );
    case 'bar':
      return (
        <div className="loading-bar" aria-hidden="true">
          <div className="loading-bar-fill" />
        </div>
      );
    case 'pulse':
      return <div className="loading-pulse" aria-hidden="true" />;
    case 'spinner':
    default:
      return (
        <div className="loading-spinner" aria-hidden="true">
          <div className="loading-spinner-ring" />
        </div>
      );
  }
}

const Bone = ({ className = '', style }) => <div className={`ui-skel ${className}`.trim()} style={style} />;

function renderSkeleton(kind, { rows, count, lines }) {
  switch (kind) {
    case 'table':
      return (
        <div className="ls-skel-table ui-table-card" aria-hidden="true">
          <div className="ls-skel-row ls-skel-row-head">
            {Array.from({ length: 5 }).map((_, i) => (
              <Bone key={i} className="ls-skel-cell" />
            ))}
          </div>
          {Array.from({ length: rows }).map((_, r) => (
            <div key={r} className="ls-skel-row">
              {Array.from({ length: 5 }).map((__, c) => (
                <Bone key={c} className="ls-skel-cell" />
              ))}
            </div>
          ))}
        </div>
      );

    case 'cards':
      return (
        <div className="ls-skel-cards card-grid" aria-hidden="true">
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="ls-skel-card">
              <Bone className="ls-skel-thumb" />
              <div className="ls-skel-card-body">
                <Bone className="ls-skel-line" style={{ width: '70%', height: 16 }} />
                <Bone className="ls-skel-line" style={{ width: '45%' }} />
                <div className="ls-skel-footer">
                  <Bone className="ls-skel-pill" />
                  <Bone className="ls-skel-pill" style={{ width: 60 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      );

    case 'list':
      return (
        <div className="ls-skel-list" aria-hidden="true">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="ls-skel-list-item">
              <Bone className="ls-skel-avatar" />
              <div className="ls-skel-list-body">
                <Bone className="ls-skel-line" style={{ width: '55%' }} />
                <Bone className="ls-skel-line" style={{ width: '80%', height: 10 }} />
              </div>
              <Bone className="ls-skel-pill" />
            </div>
          ))}
        </div>
      );

    case 'form':
      return (
        <div className="ls-skel-form" aria-hidden="true">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="ls-skel-form-row">
              <Bone className="ls-skel-line" style={{ width: 120, height: 10 }} />
              <Bone className="ls-skel-input" />
            </div>
          ))}
        </div>
      );

    case 'text':
      return (
        <div className="ls-skel-text" aria-hidden="true">
          {Array.from({ length: lines }).map((_, i) => (
            <Bone key={i} className="ls-skel-line" style={{ width: `${100 - i * 12}%` }} />
          ))}
        </div>
      );

    case 'page':
      return (
        <div className="ls-skel-page" aria-hidden="true">
          <div className="ls-skel-page-head">
            <div className="ls-skel-page-title">
              <Bone className="ls-skel-line" style={{ width: 240, height: 28 }} />
              <Bone className="ls-skel-line" style={{ width: 320, maxWidth: '100%', height: 14 }} />
            </div>
            <Bone className="ls-skel-pill" style={{ width: 120, height: 40 }} />
          </div>
          <div className="ls-skel-stats kpi-row">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ls-skel-stat">
                <Bone className="ls-skel-line" style={{ width: '50%', height: 12 }} />
                <Bone className="ls-skel-line" style={{ width: '70%', height: 28 }} />
              </div>
            ))}
          </div>
          {renderSkeleton('table', { rows: 5, count, lines })}
        </div>
      );

    default:
      return <Bone className="ls-skel-line" />;
  }
}

export default LoadingState;
